import { TypeChecker } from '@attain-apps/type-guards'

/**
 * @see {@link NumberObjectHelper.constructor.Params.irrelevancePrecision}
 */
const DEFAULT_IRRELEVANCE_PRECISION = 1

/**
 * Several utilities for working with a given object type, where that object type has some properties that are numerical
 */
export abstract class NumberObjectHelper<
  TAmountObject extends Object.Constraint,
> {
  /**
   * @see {@link NumberObjectHelper.constructor.Params.keys}
   */
  protected readonly keys: readonly Key.In.Union<TAmountObject>[]
  /**
   * @see {@link NumberObjectHelper.constructor.Params.irrelevancePrecision}
   */
  private readonly _irrelevancePrecision: number

  constructor({
    keys,
    irrelevancePrecision = DEFAULT_IRRELEVANCE_PRECISION,
  }: Readonly<NumberObjectHelper.constructor.Params<TAmountObject>>) {
    this.keys = keys
    this._irrelevancePrecision = irrelevancePrecision
  }

  /**
   * Combines multiple amount objects into one, primarily by adding the amounts
   * together
   */
  // NOTE: we allow `undefined` just to make it easier for consumers who have
  // optional values
  public combine(
    ...objects: readonly (TAmountObject | undefined)[]
  ): TAmountObject
  // NOTE: this overload allows Readonly.Deep<TAmountObject> to be passed
  public combine(
    ...objects: readonly (Readonly.Deep<TAmountObject> | undefined)[]
  ): Readonly.Deep<TAmountObject>
  public combine(
    ...objects: readonly (Readonly.Deep<TAmountObject> | undefined)[]
  ): Readonly.Deep<TAmountObject> {
    const result: Readonly.Deep<TAmountObject> = this.zero()
    for (const object of objects) {
      if (!object) {
        // Skip undefined
        continue
      }
      for (const key of this.keys) {
        const prop = object[key]
        if (TypeChecker.isNullish(prop)) {
          // Ignore nullish values
          continue
        } else if (TypeChecker.isNullish(result[key])) {
          // If there is nothing currently there, just insert the new
          // value
          result[key] = prop
        } else if (
          TypeChecker.isNumber(prop) &&
          TypeChecker.isNumber(result[key])
        ) {
          // If both the result and the current value have a number, those numbers can be added together
          ;(result as Record<typeof key, number>)[key] += prop
          // NOTE: the above cast should be consistent with the above TypeCheckers
        }
      }
    }
    return result
  }

  /**
   * Indicates the {@link TAmountObject} is effectively empty; i.e., can
   * generally be ignored
   */
  // NOTE: accepts `undefined` just to make it easier to use in cases where
  // values might be undefined
  public isEmpty(values: TAmountObject | undefined): boolean {
    return (
      values === undefined ||
      this.keys.every(key => this.isIrrelevantValue(values[key]))
    )
  }

  /**
   * Determines if a value is *not* worth considering; i.e., can be considered
   * empty for the purpose of {@link isEmpty}
   *
   * By default, this is any value that is falsy or approximately zero
   *
   * **NOTE:** this is a pretty general checker. It may not be appropriate for
   * all cases. Feel free to override as needed
   */
  protected isIrrelevantValue(val: unknown): boolean {
    return (
      TypeChecker.isFalsish(val) ||
      (TypeChecker.isNumber(val) && this.isIrrelevantNumber(val))
    )
  }

  /**
   * Used by {@link isIrrelevantValue} to determine if a non-zero number is
   * irrelevant
   *
   * Default implementation is to consider numbers that are approximately 0 to
   * be irrelevant
   *
   * @see {@link NumberObjectHelper.constructor.Params.irrelevancePrecision}
   */
  protected isIrrelevantNumber(val: number): boolean {
    return Math.round(val * this._irrelevancePrecision) === 0
  }

  /**
   * Returns an {@link Values} object with all *required* values set to 0
   *
   * Useful as a starting value when summing {@link Values}
   *
   * @example
   * const totalAmounts = items.reduce(
   *   (total, item) => Values.add(total, item.amount),
   *   Values.zero()
   * )
   */
  public abstract zero(): TAmountObject
}
export namespace NumberObjectHelper {
  export namespace constructor {
    export interface Params<TAmountObject extends Object.Constraint> {
      /**
       * These keys are used in various helper functions to iterate through the
       * relevant keys of a {@link TAmountObject}
       *
       * **NOTE:** this list *MUST* be exhaustive as some helpers will use these
       * keys to create new objects, so if there are keys missing here, they
       * will be missing in objects created by those helpers
       *
       * @see {@link NumberObjectHelper.add}, {@link NumberObjectHelper.isEmpty}
       */
      keys: readonly Key.In.Union<TAmountObject>[]
      /**
       * Determines how close to 0 a number has to be to be considered
       * irrelevant
       *
       * @default 1
       * @example
       * // irrelevancePrecision = 1
       * const relevant = [ 0.5, 1 ]
       * const irrelevant = [ 0.1, 0.05 ]
       *
       * // irrelevancePrecision = 10
       * const relevant = [ 0.05, 0.1 ]
       * const irrelevant = [ 0.01, 0.005 ]
       *
       * // irrelevancePrecision = 100 (1e2)
       * const relevant = [ 0.005, 0.01 ]
       * const irrelevant = [ 0.001, 0.0005 ]
       */
      irrelevancePrecision?: number
    }
  }
}
